import React, { useState } from 'react';
import { AlertCircle, Key, User, Shield, CheckCircle, X } from 'lucide-react';
import '../styles/PasswordReset.css';

const PasswordResetAdmin = () => {
    const [emailId, setEmailId] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!emailId.trim()) {
            setError('Email ID is required');
            return;
        }

        setLoading(true);
        setError('');
        setResult(null);

        try {
            // Get auth token from localStorage or context
            const token = localStorage.getItem('token'); // Adjust based on your auth implementation

            const response = await fetch(`${process.env.REACT_APP_API_URL}/reset-default-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}` // Adjust based on your auth implementation
                },
                body: JSON.stringify({ email_id: emailId })
            });

            const data = await response.json();

            if (response.ok) {
                setResult({
                    success: true,
                    message: data.message,
                    emailId: data.email_id,
                    defaultPassword: data.defaultPassword
                });
                setEmailId(''); // Clear form
            } else {
                setError(data.message || 'Failed to reset password');
            }
        } catch (err) {
            setError('Network error occurred. Please try again.');
            console.error('Password reset error:', err);
        } finally {
            setLoading(false);
        }
    };

    const clearMessages = () => {
        setResult(null);
        setError('');
    };

    return (
        <div className="password-reset-container">
            <div className="password-reset-wrapper">
                {/* Header */}
                <div className="header-card">
                    <div className="header-content">
                        <Shield className="header-icon" size={24} />
                        <h1 className="header-title">Admin Password Reset</h1>
                    </div>
                    <p className="header-description">Reset employee passwords to default value (Super-Admin only)</p>
                </div>

                {/* Warning Notice */}
                <div className="warning-notice">
                    <AlertCircle className="warning-icon" size={20} />
                    <div>
                        <h3 className="warning-title">Security Warning</h3>
                        <p className="warning-text">
                            This action will reset the employee's password to the default value and force them to change it on next login.
                            Use this feature responsibly and only when necessary.
                        </p>
                    </div>
                </div>

                {/* Reset Form */}
                <div className="form-card">
                    <div className="form-content">
                        <div className="input-group">
                            <label htmlFor="empId" className="input-label">
                                Email ID
                            </label>
                            <div className="input-wrapper">
                                <User className="input-icon" size={20} />
                                <input
                                    type="text"
                                    id="empId"
                                    value={emailId}
                                    onChange={(e) => {
                                        setEmailId(e.target.value);
                                        clearMessages();
                                    }}

                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            handleSubmit(e);
                                        }
                                    }}
                                    className="input-field"
                                    placeholder="Enter employee email"
                                    disabled={loading}
                                />
                            </div>
                        </div>

                        <button
                            onClick={handleSubmit}
                            disabled={loading || !emailId.trim()}
                            className="submitpd-button"
                        >
                            {loading ? (
                                <>
                                    <div className="loading-spinner"></div>
                                    Resetting Password...
                                </>
                            ) : (
                                <>
                                    <Key size={20} />
                                    Reset to Default Password
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {/* Success Message */}
                {result && result.success && (
                    <div className="success-message">
                        <div className="success-content">
                            <CheckCircle className="success-icon" size={20} />
                            <div className="success-body">
                                <h3 className="success-title">Password Reset Successful</h3>
                                <p className="success-text">{result.message}</p>
                                <div className="success-details">
                                    <div className="success-detail-row">
                                        <span className="success-detail-label">Email ID:</span>
                                        <code className="success-detail-value">{result.emailId || result.empId}</code>
                                    </div>
                                    {result.defaultPassword && (
                                        <div className="success-detail-row">
                                            <span className="success-detail-label">Default Password:</span>
                                            <code className="success-detail-value">{result.defaultPassword}</code>
                                        </div>
                                    )}
                                </div>
                                <p className="success-note">
                                    The employee will be required to change their password on next login.
                                </p>
                            </div>
                            <button
                                onClick={clearMessages}
                                className="close-button"
                            >
                                <X size={16} />
                            </button>
                        </div>
                    </div>
                )}

                {/* Error Message */}
                {error && (
                    <div className="error-message">
                        <div className="error-content">
                            <AlertCircle className="error-icon" size={20} />
                            <div className="error-body">
                                <h3 className="error-title">Error</h3>
                                <p className="error-text">{error}</p>
                            </div>
                            <button
                                onClick={clearMessages}
                                className="error-close-button"
                            >
                                <X size={16} />
                            </button>
                        </div>
                    </div>
                )}

                {/* Security Information */}
                <div className="security-info">
                    <div className="security-content">
                        <Shield className="security-icon" size={20} />
                        <div>
                            <h3 className="security-title">Security Information</h3>
                            <ul className="security-list">
                                <li>• Only Super-Admin users can perform password resets</li>
                                <li>• Default password is set to: <code className="security-code">Pumo@123</code></li>
                                <li>• Employees will be forced to change password on next login</li>
                                <li>• All password reset actions are logged for security auditing</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PasswordResetAdmin;